import React, { useState, useEffect, CSSProperties } from 'react';
import Axios from 'helpers/Interceptor';
import dayjs from 'dayjs';
import { Redirect } from 'react-router-dom';
import Loader from 'elements/Loader';
import { DataGrid } from '@mui/x-data-grid';
import { DownloadOutlined } from '@mui/icons-material';
import useUser from 'Hooks/useUser';
import ButtonSymphony from 'elements/ButtonSymphony';
import InputSymphony, { SelectOptionItem } from 'elements/InputSymphony';
import { AffiliatePartnerSnapshotResponse, getAffiliatePartners_api, getAffiliatePartnerSnapshot_api, PartnerDiscountType } from 'api/adminApi';
import { makeKey, styx } from 'modules/Utils';
import useScreen from 'Hooks/useScreen';
import Sty from 'modules/Sty';
import CardSymphony from 'elements/CardSymphony';
import PageSymphony from 'elements/PageSymphony';
import { TabItem } from 'elements/TabsSymphony';
import TabsSymphony from 'elements/TabsSymphony';
import TabSymphony from 'elements/TabSymphony';
import Stat from 'elements/Stat';
import { StatItem } from 'elements/Stat';

/** DataGrid column definitions for customer data */
const columns = [
    { field: 'name', headerName: 'Name', width: 200, sortable: true },
    { field: 'email', headerName: 'Email', width: 250, sortable: true },
    { field: 'signupDate', headerName: 'Signup Date', width: 130, sortable: true },
    { field: 'subscriptionStatus', headerName: 'Current Status', width: 160, sortable: true },
    { field: 'daysUntilFirstCampaign', headerName: 'Days Until First Campaign', width: 220, sortable: true, type: 'number' },
    { field: 'conversionsEnabled', headerName: 'User Enabled Conversions', width: 180, sortable: true },
    { field: 'totalSpend', headerName: 'Total Spend', width: 130, sortable: true, type: 'number' },
    { field: 'nonDraftCampaigns', headerName: 'Non-Draft Campaigns', width: 200, sortable: true, type: 'number' },
    { field: 'draftCampaigns', headerName: 'Draft Campaigns', width: 180, sortable: true, type: 'number' },
];

const mainTabs: TabItem[] = [
    {
        id: 'overview',
        title: 'Overview',
    },
    {
        id: 'users',
        title: 'Users',
    },
    {
        id: 'payments',
        title: 'Payments',
    },
    {
        id: 'trials',
        title: 'Trial Conversion',
    }
];

/** Component to display customer data in a grid */
const CustomerDataGrid = ({ customerDetails }: { customerDetails: CustomerDetail[] }) => {
    // Sort customer details by draftCampaigns in descending order, then by nonDraftCampaigns in descending order
    const sortedDetails = [...customerDetails]
        .sort((a, b) => {
            if (b.draftCampaigns !== a.draftCampaigns) {
                return b.draftCampaigns - a.draftCampaigns;
            }
            return b.nonDraftCampaigns - a.nonDraftCampaigns;
        });

    const rows = sortedDetails.map((customer) => ({
        ...customer,
        id: customer.brandId,
        signupDate: new Date(customer.signupDate).toLocaleDateString(),
    }));

    return (
        <div style={{ height: 500, width: '80%', maxWidth: '800px' }}>
            <DataGrid
                rows={rows}
                columns={columns}
                pageSize={10}
                rowsPerPageOptions={[10, 25, 50]}
                checkboxSelection
                disableSelectionOnClick
                initialState={{
                    sorting: {
                        sortModel: [{ field: 'draftCampaigns', sort: 'desc' }],
                    },
                }}
            />
        </div>
    );
};

/** Interface for customer detail data */
interface CustomerDetail {
    subscriptionStatus: string;
    trialStartDate: string | null;
    trialStatus: string | null;
    upgradedAt: string | null;
    cancelledAt: string | null;
    cancellationReason: string | null;
    signupDate: string;
    id: string | null;
    name: string;
    email: string;
    totalCampaigns: number;
    draftCampaigns: number;
    nonDraftCampaigns: number;
    totalSpend: number;
    daysUntilFirstCampaign: number | null;
    brandId: number;
}

/** Interface for weekly breakdown metrics */
interface WeeklyBreakdownType {
    week: string;
    brandsCreated: number;
    trialsStarted: number;
    failedPayments: number;
    successfulPayments: number;
    totalRevenue: number;
    churnedUsers: number;
}

/** Interface for KPI data structure */
interface KpiDataType {
    topLevelMetrics: AffiliatePartnerSnapshotResponse;
    customerDetails?: CustomerDetail[];
    weeklyMetrics?: WeeklyBreakdownType[];
}

/** Interface for MetricsDisplay props */
interface MetricsDisplayProps {
    stats: {
        name: string | JSX.Element;
        value: number;
        format?: 'default' | 'currency' | 'percent';
    }[];
    title?: string;
}

/** Component to display metrics in a card */
const MetricsDisplay: React.FC<MetricsDisplayProps> = ({ stats, title }) => {
    const titleStyle: React.CSSProperties = {
        fontSize: 20,
        fontWeight: 500,
        color: '#000000',
        marginBottom: 16,
        padding: '0px auto 20px',
    };

    return (
        <div className="metricsDisplay flex-col w-full max-w-3xl p-6 mt-4 bg-white rounded shadow-lg flex justify-between items-center">
            {title && (
                <p style={titleStyle} className="mb-4">
                    {title}
                </p>
            )}
            <div className="metricsStatsContainer flex flex-row justify-evenly w-full">
                {stats.map((stat, index) => (
                    <div
                        className="metricsStat flex flex-col justify-between items-center text-center"
                        style={{ maxWidth: 150 }}
                        key={index}
                    >
                        <span className="metricsStatValue" style={{ fontSize: 20, fontWeight: 500 }}>
                            {stat.format === 'currency' ? (
                                stat.value.toLocaleString('en', {
                                    style: 'currency',
                                    currency: 'USD',
                                })
                            ) : stat.format === 'percent' ? (
                                `${stat.value.toFixed(1)}%`
                            ) : (
                                stat.value
                            )}
                        </span>
                        <span className="metricsStatName" style={{ fontSize: 14, color: '#666666' }}>
                            {stat.name}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
};

const AffiliatesDashboard = () => {
    const { isSymphonyAdmin: isAdmin } = useUser();
    const { mStyle, mobileView } = useScreen()

    const [loading, setLoading] = useState(true);
    const [loadingCSV, setLoadingCSV] = useState(false);
    const [kpiData, setKpiData] = useState<KpiDataType | null>(null);
    const [tableData, setTableData] = useState<WeeklyBreakdownType[]>([]);
    const [partners, setPartners] = useState<PartnerDiscountType[]>([]);
    const [selectedPartner, setSelectedPartner] = useState<PartnerDiscountType | null>(null);
    const [loadingPartners, setLoadingPartners] = useState(true);
    const [selectedTab, setSelectedTab] = useState<TabItem>(mainTabs[0]);

    /** Calculate percentage value with fixed decimal places */
    const getPercentageValue = (value: number, total: number): string => {
        return `${((value / total) * 100).toFixed(1)}%`;
    };

    /** Fetch all affiliate partners */
    const fetchPartners = async (): Promise<void> => {
        setLoadingPartners(true);
        try {
            const response = await getAffiliatePartners_api();
            if (response) {
                setPartners(response);
                setSelectedPartner(response[0]);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoadingPartners(false);
        }
    };

    /** Fetch data for the selected partner */
    const fetchPartnerSnapshot = async (partnerId: string): Promise<void> => {
        setLoading(true);
        try {
            const response = await getAffiliatePartnerSnapshot_api(partnerId);
            if (response) {
                setKpiData({
                    topLevelMetrics: response
                });
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    /** Download CSV for the selected partner's brands */
    const downloadBrandsList = async (): Promise<void> => {
        if (!selectedPartner) return;

        setLoadingCSV(true);
        try {
            const response = await Axios.post(
                '/admin/get-affiliate-user-csv',
                { partnerId: selectedPartner.partnerId },
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    responseType: 'blob',
                    params: {
                        csv: 1,
                    },
                }
            );

            // Trigger file download
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            const fileName = `${selectedPartner.partnerName || 'Partner'}_Brands_${dayjs().format('MM-DD-YYYY')}.csv`;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error(error);
        } finally {
            setLoadingCSV(false);
        }
    };

    /** Handle partner selection change */
    const onPartnerSelectionChange = async (option: SelectOptionItem): Promise<void> => {
        const partner = partners.find((p) => p.partnerId === option.value);
        if (partner) {
            setSelectedPartner(partner);
            // Clear existing data while loading new data
            setKpiData(null);
            await fetchPartnerSnapshot(partner.partnerId);
        }
    };

    /** Handle tab selection change */
    const handleTabChange = (tab: TabItem) => {
        setSelectedTab(tab);
    };

    // Initial load of partners
    useEffect(() => {
        fetchPartners();
    }, []);

    // Load partner data when selected partner changes
    useEffect(() => {
        if (selectedPartner) {
            fetchPartnerSnapshot(selectedPartner.partnerId);
        }
    }, [selectedPartner]);

    // Redirect non-admin users
    if (!isAdmin) {
        return <Redirect to="/dashboard" />;
    }

    // Show loader while fetching partners
    if (loadingPartners) {
        return <Loader />;
    }

    const renderMainContent = () => {
        if (!kpiData || loading) {
            return (
                <Loader
                    className="mainContentLoader"
                    title="Loading Affiliate Data..."
                    size={44}
                    isCentered
                />
            )
        }

        return (
            <div
                className="affiliatesDashboardComponent"
                style={styx(
                    mStyle(sty, 'campaignDetailsV2Component'),
                )}
            >
                <div
                    className="affiliatesDashboardInner"
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 16,
                        width: '100%',
                    }}
                >
                    <CardSymphony
                        className="affiliatesDashboardCard"
                        style={mStyle(sty, 'campaignDetailsV2Card')}
                    >
                        <TabsSymphony
                            className="affiliatesDashboardTabs"
                            tabs={mainTabs}
                            selectedTab={selectedTab.id}
                            onChange={(tab) => handleTabChange(tab as TabItem)}
                            isBorderBottom
                        />
                        {renderTabs()}
                    </CardSymphony>
                </div>
            </div>
        )
    }

    const renderTabs = () => {
        return (
            <>
                {renderOverviewTab()}
                {renderUsersTab()}
                {renderPaymentsTab()}
                {renderTrialsTab()}
            </>
        )
    }

    /** Render the Overview tab with summary metrics */
    const renderOverviewTab = () => {
        if (!kpiData) return null;

        const { topLevelMetrics } = kpiData;
        const {
            users,
            payments,
            campaigns,
            totalSignups,
            trialConversion,
        } = topLevelMetrics;

        // Define default empty objects with the correct structure for type safety
        const defaultStat = { active: 0, unpaid: 0, canceled: 0, past_due: 0, trialing: 0 };

        // Safely access nested properties with optional chaining and defaults
        const pro = {
            monthly: users.pro?.monthly || defaultStat,
            annual: users.pro?.annual || defaultStat
        };

        const lite = {
            monthly: users.lite?.monthly || defaultStat,
            annual: users.lite?.annual || defaultStat
        };

        const proActiveUsers = pro.monthly.active + pro.annual.active;
        const liteActiveUsers = lite.monthly.active + lite.annual.active;
        const totalActiveUsers = proActiveUsers + liteActiveUsers;

        const conversionRate = totalSignups > 0 ? (users.overall / totalSignups) * 100 : 0;
        const trialConversionRate = trialConversion.started > 0 ? (trialConversion.converted / trialConversion.started) * 100 : 0;

        const overviewStats = [
            {
                name: 'Partner Performance',
                stats: [
                    {
                        title: 'Total Signups',
                        value: totalSignups,
                        displayType: 'number'
                    },
                    {
                        title: 'Artists Created',
                        value: users.overall,
                        displayType: 'number'
                    },
                    {
                        title: 'Signup to Artist Conversion',
                        value: conversionRate,
                        displayType: 'percentage'
                    }
                ],
                subtitle: `Overview for partner: ${selectedPartner?.partnerName}`,
                icon: 'user'
            },
            {
                name: 'Subscriber Stats',
                stats: [
                    {
                        title: 'Total Paying Users',
                        value: users.paid,
                        displayType: 'number'
                    },
                    {
                        title: 'Pro Subscribers',
                        value: proActiveUsers,
                        displayType: 'number'
                    },
                    {
                        title: 'Lite Subscribers',
                        value: liteActiveUsers,
                        displayType: 'number'
                    }
                ],
                subtitle: 'Current active subscribers',
                icon: 'star'
            },
            {
                name: 'Revenue',
                stats: [
                    {
                        title: 'Gross Revenue',
                        value: payments.gross_revenue,
                        displayType: 'currency'
                    },

                    {
                        title: 'Successful Payments',
                        value: payments.successful,
                        displayType: 'number'
                    }
                ],
                subtitle: 'Total revenue generated from this partner',
                icon: 'money'
            },
            {
                name: 'Trial Conversion',
                stats: [
                    {
                        title: 'Trials Started',
                        value: trialConversion.started,
                        displayType: 'number'
                    },
                    {
                        title: 'Trials Converted',
                        value: trialConversion.converted,
                        displayType: 'number'
                    },
                    {
                        title: 'Conversion Rate',
                        value: trialConversionRate,
                        displayType: 'percentage'
                    }
                ],
                subtitle: 'Performance of trial to paid conversion',
                icon: 'boost'
            }
        ];

        return (
            <TabSymphony
                className="overviewTab"
                visible={selectedTab.id === 'overview'}
                style={sty.tabCampaignDetails}
            >
                <CardSymphony
                    className="overviewHeader"
                    title={`${selectedPartner?.partnerName} Partner Dashboard`}
                    subtitle="Overview of key partner performance metrics"
                    isNoPadding
                    size="small"
                    style={{
                        borderBottom: Sty.border,
                        borderRadius: 0,
                        paddingBottom: 4
                    }}
                />

                <div className="overviewStatContainer" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {overviewStats.map((stat: any, index) => {
                        const { name, stats, subtitle, icon } = stat;

                        const localStats = stats.map((statItem: StatItem) => {
                            return {
                                title: statItem.title,
                                value: statItem.value,
                                displayType: statItem.displayType ?? "number"
                            }
                        })

                        return (
                            <Stat
                                key={makeKey(name, index)}
                                icon={icon}
                                className="overviewStat"
                                title={name}
                                subtitle={subtitle}
                                stats={localStats}
                                isHorizontal
                            />
                        )
                    })}
                </div>

                <div style={sty.downloadContainer}>
                    <ButtonSymphony
                        className="downloadBrandsButton"
                        onClick={downloadBrandsList}
                        disabled={true || loadingCSV}
                    >
                        {!loadingCSV ?
                            <>
                                <DownloadOutlined />
                                Download Brands CSV
                            </>
                            :
                            <Loader height={18} width={18} />
                        }
                    </ButtonSymphony>
                </div>
            </TabSymphony>
        )
    }

    /** Render the Users tab with detailed user statistics */
    const renderUsersTab = () => {
        if (!kpiData) return null;

        const { topLevelMetrics } = kpiData;
        const { users, totalSignups } = topLevelMetrics;

        // Define default empty objects with the correct structure for type safety
        const defaultStat = { active: 0, unpaid: 0, canceled: 0, past_due: 0, trialing: 0 };

        // Safely access nested properties with optional chaining and defaults
        const pro = {
            monthly: users.pro?.monthly || defaultStat,
            annual: users.pro?.annual || defaultStat
        };

        const lite = {
            monthly: users.lite?.monthly || defaultStat,
            annual: users.lite?.annual || defaultStat
        };

        const free = users.free || 0;
        const overall = users.overall || 0;

        const proMonthlyStats: StatItem[] = [
            {
                title: 'Active',
                value: pro.monthly.active,
                displayType: 'number'
            },
            {
                title: 'Unpaid',
                value: pro.monthly.unpaid,
                displayType: 'number'
            },
            {
                title: 'Canceled',
                value: pro.monthly.canceled,
                displayType: 'number'
            },
            {
                title: 'Past Due',
                value: pro.monthly.past_due,
                displayType: 'number'
            },
            {
                title: 'Trialing',
                value: pro.monthly.trialing,
                displayType: 'number'
            }
        ];

        const proAnnualStats: StatItem[] = [
            {
                title: 'Active',
                value: pro.annual.active,
                displayType: 'number'
            },
            {
                title: 'Unpaid',
                value: pro.annual.unpaid,
                displayType: 'number'
            },
            {
                title: 'Canceled',
                value: pro.annual.canceled,
                displayType: 'number'
            },
            {
                title: 'Past Due',
                value: pro.annual.past_due,
                displayType: 'number'
            },
            {
                title: 'Trialing',
                value: pro.annual.trialing,
                displayType: 'number'
            }
        ];

        const liteMonthlyStats: StatItem[] = [
            {
                title: 'Active',
                value: lite.monthly.active,
                displayType: 'number'
            },
            {
                title: 'Unpaid',
                value: lite.monthly.unpaid,
                displayType: 'number'
            },
            {
                title: 'Canceled',
                value: lite.monthly.canceled,
                displayType: 'number'
            },
            {
                title: 'Past Due',
                value: lite.monthly.past_due,
                displayType: 'number'
            },
            {
                title: 'Trialing',
                value: lite.monthly.trialing,
                displayType: 'number'
            }
        ];

        const liteAnnualStats: StatItem[] = [
            {
                title: 'Active',
                value: lite.annual.active,
                displayType: 'number'
            },
            {
                title: 'Unpaid',
                value: lite.annual.unpaid,
                displayType: 'number'
            },
            {
                title: 'Canceled',
                value: lite.annual.canceled,
                displayType: 'number'
            },
            {
                title: 'Past Due',
                value: lite.annual.past_due,
                displayType: 'number'
            },
            {
                title: 'Trialing',
                value: lite.annual.trialing,
                displayType: 'number'
            }
        ];

        const overallUserStats: StatItem[] = [
            {
                title: 'Total Signups',
                value: totalSignups,
                displayType: 'number'
            },
            {
                title: 'Artists Created',
                value: overall,
                displayType: 'number'
            },
            {
                title: 'Free Users',
                value: free,
                displayType: 'number'
            },
            {
                title: 'Paid Users',
                value: users.paid || 0,
                displayType: 'number'
            },
            {
                title: 'Trialing Users',
                value: users.trialing || 0,
                displayType: 'number'
            }
        ];

        return (
            <TabSymphony
                className="usersTab"
                visible={selectedTab.id === 'users'}
                style={sty.tabCampaignDetails}
            >
                <CardSymphony
                    className="usersHeader"
                    title="User Statistics"
                    subtitle="Detailed breakdown of user statistics by plan type and billing period"
                    isNoPadding
                    size="small"
                    style={{
                        borderBottom: Sty.border,
                        borderRadius: 0,
                        paddingBottom: 4
                    }}
                />

                <Stat
                    className="overallUserStats"
                    title="Overall User Metrics"
                    subtitle={`Summary statistics for all users from partner: ${selectedPartner?.partnerName}`}
                    stats={overallUserStats}
                    isHorizontal
                    icon="user"
                />

                <div className="userStatsContainer" style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 16 }}>
                    <CardSymphony
                        className="proUsersHeader"
                        title="Pro Subscribers"
                        isNoPadding
                        size="small"
                        style={{
                            borderBottom: Sty.border,
                            borderRadius: 0,
                            paddingBottom: 4,
                            marginBottom: 8
                        }}
                    />

                    <div className="proStatsContainer" style={{ display: 'flex', flexDirection: mobileView ? 'column' : 'row', gap: 16 }}>
                        <Stat
                            isHorizontal
                            className="proMonthlyStats"
                            title="Monthly Pro"
                            subtitle="Stats for monthly Pro subscribers"
                            stats={proMonthlyStats}
                            icon="star"
                            style={{ flex: 1 }}
                        />
                        <Stat
                            isHorizontal
                            className="proAnnualStats"
                            title="Annual Pro"
                            subtitle="Stats for annual Pro subscribers"
                            stats={proAnnualStats}
                            icon="star"
                            style={{ flex: 1 }}
                        />
                    </div>

                    <CardSymphony
                        
                        className="liteUsersHeader"
                        title="Lite Subscribers"
                        isNoPadding
                        size="small"
                        style={{
                            borderBottom: Sty.border,
                            borderRadius: 0,
                            paddingBottom: 4,
                            marginBottom: 8,
                            marginTop: 16
                        }}
                    />

                    <div className="liteStatsContainer" style={{ display: 'flex', flexDirection: mobileView ? 'column' : 'row', gap: 16 }}>
                        <Stat
                            isHorizontal
                            className="liteMonthlyStats"
                            title="Monthly Lite"
                            subtitle="Stats for monthly Lite subscribers"
                            stats={liteMonthlyStats}
                            icon="star"
                            style={{ flex: 1 }}
                        />
                        <Stat
                            isHorizontal
                            className="liteAnnualStats"
                            title="Annual Lite"
                            subtitle="Stats for annual Lite subscribers"
                            stats={liteAnnualStats}
                            icon="star"
                            style={{ flex: 1 }}
                        />
                    </div>
                </div>
            </TabSymphony>
        );
    }

    /** Render the Payments tab with detailed payment statistics */
    const renderPaymentsTab = () => {
        if (!kpiData) return null;

        const { topLevelMetrics } = kpiData;
        const { payments } = topLevelMetrics;

        // Define default empty objects with the correct structure for type safety
        const defaultPaymentStat = { net: 0, count: 0, gross: 0 };

        // Safely access nested properties with optional chaining and defaults
        const pro = {
            monthly: payments.pro?.monthly || defaultPaymentStat,
            annual: payments.pro?.annual || defaultPaymentStat
        };

        const lite = {
            monthly: payments.lite?.monthly || defaultPaymentStat,
            annual: payments.lite?.annual || defaultPaymentStat
        };

        const successful = payments.successful || 0;
        const gross_revenue = payments.gross_revenue || 0;
        const net_revenue = payments.net_revenue || gross_revenue;

        const overallPaymentStats: StatItem[] = [
            {
                title: 'Successful Payments',
                value: successful,
                displayType: 'number'
            },
            {
                title: 'Gross Revenue',
                value: gross_revenue,
                displayType: 'currency'
            },
          
        ];

        const proMonthlyPaymentStats: StatItem[] = [
            {
                title: 'Payment Count',
                value: pro.monthly.count,
                displayType: 'number'
            },
            {
                title: 'Gross Revenue',
                value: pro.monthly.gross,
                displayType: 'currency'
            }
        ];

        const proAnnualPaymentStats: StatItem[] = [
            {
                title: 'Payment Count',
                value: pro.annual.count,
                displayType: 'number'
            },
            {
                title: 'Gross Revenue',
                value: pro.annual.gross,
                displayType: 'currency'
            }
        ];

        const liteMonthlyPaymentStats: StatItem[] = [
            {
                title: 'Payment Count',
                value: lite.monthly.count,
                displayType: 'number'
            },
            {
                title: 'Gross Revenue',
                value: lite.monthly.gross,
                displayType: 'currency'
            }
        ];

        const liteAnnualPaymentStats: StatItem[] = [
            {
                title: 'Payment Count',
                value: lite.annual.count,
                displayType: 'number'
            },
            {
                title: 'Gross Revenue',
                value: lite.annual.gross,
                displayType: 'currency'
            }
        ];

        return (
            <TabSymphony
                className="paymentsTab"
                visible={selectedTab.id === 'payments'}
                style={sty.tabCampaignDetails}
            >
                <CardSymphony
                    className="paymentsHeader"
                    title="Payment Statistics"
                    subtitle="Detailed breakdown of payment statistics by plan type and billing period"
                    isNoPadding
                    size="small"
                    style={{
                        borderBottom: Sty.border,
                        borderRadius: 0,
                        paddingBottom: 4
                    }}
                />

                <Stat
                    className="overallPaymentStats"
                    title="Overall Payment Metrics"
                    subtitle={`Summary payment statistics from partner: ${selectedPartner?.partnerName}`}
                    stats={overallPaymentStats}
                    isHorizontal
                    icon="money"
                />

                <div className="paymentStatsContainer" style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 16 }}>
                    <CardSymphony
                        className="proPaymentsHeader"
                        title="Pro Plan Payments"
                        isNoPadding
                        size="small"
                        style={{
                            borderBottom: Sty.border,
                            borderRadius: 0,
                            paddingBottom: 4,
                            marginBottom: 8
                        }}
                    />

                    <div className="proPaymentsContainer" style={{ display: 'flex', flexDirection: mobileView ? 'column' : 'row', gap: 16 }}>
                        <Stat
                            className="proMonthlyPaymentStats"
                            title="Monthly Pro Payments"
                            subtitle="Revenue from monthly Pro subscriptions"
                            stats={proMonthlyPaymentStats}
                            icon="money"
                            isHorizontal
                            style={{ flex: 1 }}
                        />
                        <Stat
                            className="proAnnualPaymentStats"
                            title="Annual Pro Payments"
                            subtitle="Revenue from annual Pro subscriptions"
                            stats={proAnnualPaymentStats}
                            icon="money"
                            isHorizontal
                            style={{ flex: 1 }}
                        />
                    </div>

                    <CardSymphony
                        className="litePaymentsHeader"
                        title="Lite Plan Payments"
                        isNoPadding

                        size="small"
                        style={{
                            borderBottom: Sty.border,
                            borderRadius: 0,
                            paddingBottom: 4,
                            marginBottom: 8,
                            marginTop: 16
                        }}
                    />

                    <div className="litePaymentsContainer" style={{ display: 'flex', flexDirection: mobileView ? 'column' : 'row', gap: 16 }}>
                        <Stat
                            isHorizontal
                            className="liteMonthlyPaymentStats"
                            title="Monthly Lite Payments"
                            subtitle="Revenue from monthly Lite subscriptions"
                            stats={liteMonthlyPaymentStats}
                            icon="money"
                            style={{ flex: 1 }}
                        />
                        <Stat
                            isHorizontal
                            className="liteAnnualPaymentStats"
                            title="Annual Lite Payments"
                            subtitle="Revenue from annual Lite subscriptions"
                            stats={liteAnnualPaymentStats}
                            icon="money"
                            style={{ flex: 1 }}
                        />
                    </div>
                </div>
            </TabSymphony>
        );
    }

    /** Render the Trials tab with detailed trial conversion metrics */
    const renderTrialsTab = () => {
        if (!kpiData) return null;

        const { topLevelMetrics } = kpiData;
        const { trialConversion } = topLevelMetrics;

        // Define default empty objects with the correct structure for type safety
        const defaultTrialStat = { started: 0, canceled: 0, converted: 0, payment_failure: 0 };

        // Safely access nested properties with optional chaining and defaults
        const pro = {
            monthly: trialConversion.pro?.monthly || defaultTrialStat,
            annual: trialConversion.pro?.annual || defaultTrialStat
        };

        const lite = {
            monthly: trialConversion.lite?.monthly || defaultTrialStat,
            annual: trialConversion.lite?.annual || defaultTrialStat
        };

        const started = trialConversion.started || 0;
        const converted = trialConversion.converted || 0;
        const canceled = trialConversion.canceled || 0;
        const trialing = trialConversion.trialing || 0;
        const payment_failure_cancellation = trialConversion.payment_failure_cancellation || 0;

        const conversionRate = started > 0 ? (converted / started) * 100 : 0;
        const cancellationRate = started > 0 ? (canceled / started) * 100 : 0;
        const paymentFailureRate = started > 0 ? (payment_failure_cancellation / started) * 100 : 0;

        const overallTrialStats: StatItem[] = [
            {
                title: 'Trials Started',
                value: started,
                displayType: 'number'
            },
            {
                title: 'Converted to Paid',
                value: converted,
                displayType: 'number'
            },
            {
                title: 'Conversion Rate',
                value: conversionRate,
                displayType: 'percentage'
            },
            // {
            //     title: 'Canceled by User',
            //     value: canceled,
            //     displayType: 'number'
            // },
            // {
            //     title: 'Payment Failures',
            //     value: payment_failure_cancellation,
            //     displayType: 'number'
            // }
        ];

        const proMonthlyTrialStats: StatItem[] = [
            {
                title: 'Trials Started',
                value: pro.monthly.started,
                displayType: 'number'
            },
            {
                title: 'Converted to Paid',
                value: pro.monthly.converted,
                displayType: 'number'
            },
            {
                title: 'Conversion Rate',
                value: pro.monthly.started > 0 ? (pro.monthly.converted / pro.monthly.started) * 100 : 0,
                displayType: 'percentage'
            },
            {
                title: 'Canceled by User',
                value: pro.monthly.canceled,
                displayType: 'number'
            },
            {
                title: 'Payment Failures',
                value: pro.monthly.payment_failure,
                displayType: 'number'
            }
        ];

        const proAnnualTrialStats: StatItem[] = [
            {
                title: 'Trials Started',
                value: pro.annual.started,
                displayType: 'number'
            },
            {
                title: 'Converted to Paid',
                value: pro.annual.converted,
                displayType: 'number'
            },
            {
                title: 'Conversion Rate',
                value: pro.annual.started > 0 ? (pro.annual.converted / pro.annual.started) * 100 : 0,
                displayType: 'percentage'
            },
            {
                title: 'Canceled by User',
                value: pro.annual.canceled,
                displayType: 'number'
            },
            {
                title: 'Payment Failures',
                value: pro.annual.payment_failure,
                displayType: 'number'
            }
        ];

        const liteMonthlyTrialStats: StatItem[] = [
            {
                title: 'Trials Started',
                value: lite.monthly.started,
                displayType: 'number'
            },
            {
                title: 'Converted to Paid',
                value: lite.monthly.converted,
                displayType: 'number'
            },
            {
                title: 'Conversion Rate',
                value: lite.monthly.started > 0 ? (lite.monthly.converted / lite.monthly.started) * 100 : 0,
                displayType: 'percentage'
            },
            {
                title: 'Canceled by User',
                value: lite.monthly.canceled,
                displayType: 'number'
            },
            {
                title: 'Payment Failures',
                value: lite.monthly.payment_failure,
                displayType: 'number'
            }
        ];

        const liteAnnualTrialStats: StatItem[] = [
            {
                title: 'Trials Started',
                value: lite.annual.started,
                displayType: 'number'
            },
            {
                title: 'Converted to Paid',
                value: lite.annual.converted,
                displayType: 'number'
            },
            {
                title: 'Conversion Rate',
                value: lite.annual.started > 0 ? (lite.annual.converted / lite.annual.started) * 100 : 0,
                displayType: 'percentage'
            },
            {
                title: 'Canceled by User',
                value: lite.annual.canceled,
                displayType: 'number'
            },
            {
                title: 'Payment Failures',
                value: lite.annual.payment_failure,
                displayType: 'number'
            }
        ];

        return (
            <TabSymphony
                className="trialsTab"
                visible={selectedTab.id === 'trials'}
                style={sty.tabCampaignDetails}
            >
                <CardSymphony
                    className="trialsHeader"
                    title="Trial Conversion Metrics"
                    subtitle="Detailed breakdown of trial conversion metrics by plan type and billing period"
                    isNoPadding
                    size="small"
                    style={{
                        borderBottom: Sty.border,
                        borderRadius: 0,
                        paddingBottom: 4
                    }}
                />

                <Stat
                    className="overallTrialStats"
                    title="Overall Trial Conversion"
                    subtitle={`Summary trial statistics from partner: ${selectedPartner?.partnerName}`}
                    stats={overallTrialStats}
                    isHorizontal
                    icon="boost"
                />

                <div className="trialStatsContainer" style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 16 }}>
                    <CardSymphony
                        className="proTrialsHeader"
                        title="Pro Plan Trial Conversions"
                        isNoPadding
                        size="small"
                        style={{
                            borderBottom: Sty.border,
                            borderRadius: 0,
                            paddingBottom: 4,
                            marginBottom: 8
                        }}
                    />

                    <div className="proTrialsContainer" style={{ display: 'flex', flexDirection: mobileView ? 'column' : 'row', gap: 16 }}>
                        <Stat
                            isHorizontal
                            className="proMonthlyTrialStats"
                            title="Monthly Pro Trials"
                            subtitle="Conversion metrics for monthly Pro trials"
                            stats={proMonthlyTrialStats}
                            icon="boost"
                            style={{ flex: 1 }}
                        />
                        <Stat
                            isHorizontal
                            className="proAnnualTrialStats"
                            title="Annual Pro Trials"
                            subtitle="Conversion metrics for annual Pro trials"
                            stats={proAnnualTrialStats}
                            icon="boost"
                            style={{ flex: 1 }}
                        />
                    </div>

                    <CardSymphony
                        className="liteTrialsHeader"
                        title="Lite Plan Trial Conversions"
                        isNoPadding
                        size="small"
                        style={{
                            borderBottom: Sty.border,
                            borderRadius: 0,
                            paddingBottom: 4,
                            marginBottom: 8,
                            marginTop: 16
                        }}
                    />

                    <div className="liteTrialsContainer" style={{ display: 'flex', flexDirection: mobileView ? 'column' : 'row', gap: 16 }}>
                        <Stat
                            isHorizontal
                            className="liteMonthlyTrialStats"
                            title="Monthly Lite Trials"
                            subtitle="Conversion metrics for monthly Lite trials"
                            stats={liteMonthlyTrialStats}
                            icon="boost"
                            style={{ flex: 1 }}
                        />
                        <Stat
                            isHorizontal
                            className="liteAnnualTrialStats"
                            title="Annual Lite Trials"
                            subtitle="Conversion metrics for annual Lite trials"
                            stats={liteAnnualTrialStats}
                            icon="boost"
                            style={{ flex: 1 }}
                        />
                    </div>
                </div>
            </TabSymphony>
        );
    }

    const renderCustomerDataGrid = () => {
        if (!kpiData || !kpiData.customerDetails) {
            return null;
        }

        return (
            <CustomerDataGrid customerDetails={kpiData.customerDetails} />
        );
    }

    const renderMetricsDisplay = () => {
        if (!kpiData || !kpiData.topLevelMetrics.payments) {
            return null;
        }

        const { payments } = kpiData.topLevelMetrics;

        return (
            <MetricsDisplay
                stats={[
                    {
                        name: "Number of successful payments",
                        value: payments.successful
                    },
                    {
                        name: "Gross Revenue from Partner",
                        value: payments.gross_revenue,
                        format: 'currency'
                    },
                ]}
                title="Successful Payment Metrics"
            />
        );
    }

    const renderTrialConversionMetrics = () => {
        if (!kpiData || !kpiData.topLevelMetrics.trialConversion) {
            return null;
        }

        const { trialConversion } = kpiData.topLevelMetrics;

        return (
            <MetricsDisplay
                stats={[
                    {
                        name: (
                            <div>
                                <p style={sty.textBold}>Total Trials Started</p>
                                <p style={sty.textLight}>100% of the Funnel</p>
                            </div>
                        ),
                        value: trialConversion.started
                    },
                    {
                        name: (
                            <div>
                                <p style={sty.textBold}>Total Converted Users</p>
                                <p style={sty.textLight}>{getPercentageValue(trialConversion.converted, trialConversion.started)} of trial starts converted to Pro</p>
                            </div>
                        ),
                        value: trialConversion.converted
                    },
                    {
                        name: (
                            <div>
                                <p style={sty.textBold}>Churned trials due to user cancelling</p>
                                <p style={sty.textLight}>{getPercentageValue(trialConversion.canceled, trialConversion.started)} of trials cancelled</p>
                            </div>
                        ),
                        value: trialConversion.canceled
                    },
                    {
                        name: (
                            <div>
                                <p style={sty.textBold}>Churned trials due to payment failure</p>
                                <p style={sty.textLight}>{getPercentageValue(trialConversion.payment_failure_cancellation, trialConversion.started)} of trials cancelled</p>
                            </div>
                        ),
                        value: trialConversion.payment_failure_cancellation
                    },
                ]}
                title="Trial Conversion Metrics"
            />
        );
    }

    return (
        <PageSymphony
            className="affiliatesDashboardPage"
            isContent
        >
            <div className="affiliatesDashboardContainer" style={sty.dashboardContainer}>
                <div className="partnerSelectorContainer" style={sty.partnerSelectorContainer}>
                    <CardSymphony
                        className="partnerSelectorCard"
                        title="Symphony Partner Dashboard"
                        subtitle="Select a partner to view their performance metrics"
                        size="small"
                    >
                        <InputSymphony
                            className="partnerSelector"
                            value={selectedPartner?.partnerId || ''}
                            onOptionSelect={onPartnerSelectionChange}
                            isSelectDropdown
                            options={partners.map((partner) => ({
                                value: partner.partnerId,
                                title: partner.partnerName,
                            } as SelectOptionItem))}
                            placeholder="Select a partner"
                        />
                    </CardSymphony>
                </div>

                {selectedPartner && renderMainContent()}
            </div>
        </PageSymphony>
    );
};

export default AffiliatesDashboard;

const sty: Record<string, CSSProperties> = {
    campaignDetailsV2Component: {
        display: 'flex',
        gap: 16,
        width: '100%',
    },
    campaignDetailsV2Component_mobile: {
        flexDirection: 'column',
    },
    campaignDetailsV2Card: {
        padding: 16,
    },
    campaignDetailsV2Card_mobile: {
        padding: 8,
    },
    tabCampaignDetails: {
        gap: 16,
    },
    dashboardContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        padding: 16,
        gap: 24,
        width: '100%',
        maxWidth: 1200,
        margin: '0 auto',
    },
    partnerSelectorContainer: {
        width: '100%',
    },
    downloadContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: 24,
    },
    textLight: {
        color: '#808080',
        fontSize: 12,
        fontWeight: 400,
    },
    textBold: {
        fontWeight: 500,
        fontSize: 14,
    },
};